import {
    RightModal,
    RightModalContent,
    RightModalTitle,
} from "@app/components/RightModal";
import { SubthemeTag } from "@app/shared/components/Theme/SubthemeTag";
import { ThemeTag } from "@app/shared/components/Theme/ThemeTag";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { isExampleDrawerOpen } from "./ctx";
import { RESOURCE_NAME, keys } from "./i18n";
import { TargetExampleDisplay } from "./TargetExampleDisplay";
import { useExampleData } from "./useExampleData";

export const ExampleDrawer = () => {
    const [isOpen, setIsOpen] = useAtom(isExampleDrawerOpen);
    const handleOpenChange = (open: boolean) => setIsOpen(open);

    const { objective, indicator, targets } = useExampleData();

    const { t } = useTranslation(RESOURCE_NAME);

    return (
        <RightModal open={isOpen} onOpenChange={handleOpenChange}>
            <RightModalContent>
                <FlexCol gap="5">
                    <RightModalTitle className="p-6 pb-0">
                        <FlexCol gap="5">
                            <Text
                                variant="body-xs"
                                color="secondary"
                                className="uppercase"
                            >
                                {t(keys.example_title)}
                            </Text>
                            <Text variant="header2" color="primary">
                                {objective.name}
                            </Text>
                        </FlexCol>
                    </RightModalTitle>
                    <FlexCol gap="5" px="6" pb="6">
                        <FlexRow gap="3">
                            <ThemeTag size="md" themeId={objective.themeId} />
                            <SubthemeTag
                                size="md"
                                subthemeId={objective.subthemeId}
                                themeId={objective.themeId}
                            />
                        </FlexRow>
                        <FlexCol
                            gap="5"
                            p="5"
                            br="lg"
                            className="border border-secondary"
                        >
                            <Text variant="body-bold">
                                {t(keys.indicator_section_title)}
                            </Text>
                            <Text variant="body">{indicator.name}</Text>
                            <Text variant="body-bold">
                                {t(keys.targets_title)}
                            </Text>
                            <FlexCol alignItems="start" gap="4">
                                {targets.map((target) => (
                                    <FlexCol
                                        key={target.id}
                                        gap="0"
                                        alignSelf="stretch"
                                    >
                                        <TargetExampleDisplay
                                            target={target}
                                            unitShortName={
                                                indicator.unit.shortName
                                            }
                                        />
                                    </FlexCol>
                                ))}
                            </FlexCol>
                        </FlexCol>
                        <FlexCol
                            gap="5"
                            p="5"
                            br="lg"
                            className="border border-secondary"
                        >
                            <Text variant="body-bold">
                                {t(keys.description_title)}
                            </Text>
                            <Text variant="body">{objective.description}</Text>
                        </FlexCol>
                        <FlexCol
                            gap="5"
                            p="5"
                            br="lg"
                            className="border border-secondary"
                        >
                            <Text variant="body-bold">
                                {t(keys.action_plan_title)}
                            </Text>
                            <ReactMarkdown
                                className="markdown"
                                remarkPlugins={[remarkBreaks]}
                            >
                                {objective.actionPlan}
                            </ReactMarkdown>
                        </FlexCol>
                    </FlexCol>
                </FlexCol>
            </RightModalContent>
        </RightModal>
    );
};
